import { useCallback, useContext } from "react";
import { environment } from "../config/environment";
import { TokenContext } from "../context/TokenContext";
import { TenantContext } from "../context/TenantContextProvider";

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export type DownloadOrganisationFileResult = { content: string; name: string; contentType: string; extension?: string };

export function useDownloadOrganisationFile() {
  const { token } = useContext(TokenContext);
  const { tenant } = useContext(TenantContext);

  const downloadOrganisationFile = useCallback(
    async (
      fileId: string,
      fileName: string,
      fileExtension?: string
    ): Promise<DownloadOrganisationFileResult | null> => {
      if (!token) return null;

      const url = `${environment.apiUrl}/api/files/${fileId}/download`;
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
        ...(tenant ? { "Business-Tenant": tenant } : {}),
      };

      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) return null;

      const buffer = await res.arrayBuffer();
      const content = arrayBufferToBase64(buffer);
      const name =
        fileName || res.headers.get("Content-Disposition")?.match(/filename="?([^";\n]+)"?/)?.[1] || "attachment";

      return {
        content,
        name,
        contentType: res.headers.get("Content-Type") || "",
        extension: fileExtension,
      };
    },
    [token, tenant]
  );

  return { downloadOrganisationFile };
}
